import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  AHIcon,
  AHText,
  AHTextFonts,
  Box,
  ListItem,
  useLayoutContext,
  useScaleFactor,
  useTheme,
} from "arkh";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { totalHours } from "widgets/src/WidgetEmployeeSchedule/totalHours";

import { TimeBackgroundLines } from "widgets/src/WidgetEmployeeSchedule/TimeBackgroundLines";
import { View } from "react-native";
import { DateTime } from "luxon";
import { EmployeeScheduleScreenQuery } from "./__generated__/EmployeeScheduleScreenQuery.graphql";
import { Employee } from "./EmployeeScheduleShifts";
import { NamePanel } from "./EmployeeScheduleName";
import { ZoomControls } from "./ZoomControls";
import Animated, {
  scrollTo,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import {
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "@/src/common/navigationHeaderOptions";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { RouteProp } from "@react-navigation/native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { scheduleOnUI } from "react-native-worklets";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { useHasPermissions } from "@/src/OS/Login/usePermissions";

type ParamList = {
  EmployeeScheduleMain: {
    teamIds: string[];
  };
};
const Stack = createNativeStackNavigator<ParamList>();

const EmployeeScheduleScreenComponent = (
  props: UserLoggedInNavigatorScreenProps<"EmployeeSchedule">
) => {
  const getNavigationHeaderOptoins = useNavigationHeaderOptions();
  const intl_tokens = useIntlTokens();

  return (
    <Stack.Navigator>
      <Stack.Screen
        name="EmployeeScheduleMain"
        component={EmployeeScheduleMainScreen}
        initialParams={{
          teamIds: props.route.params.teamIds,
        }}
        options={getNavigationHeaderOptoins({
          type: "CLOSE",
          title: intl_tokens.EmployeeSchedule,
          disableBorder: true,
          onClose: () => {
            props.navigation.goBack();
          },
        })}
      />
    </Stack.Navigator>
  );
};
export const EmployeeScheduleScreen = withWebModal(
  EmployeeScheduleScreenComponent,
  true,
  {
    width: 1200,
  }
);

function EmployeeScheduleMainScreen(props: {
  route: RouteProp<ParamList, "EmployeeScheduleMain">;
  navigation: UserLoggedInNavigatorNavigationProp<"EmployeeScheduleEdit">;
}) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <EmployeeScheduleMainScreenComponent
          fetchKey={fk}
          teamIds={props.route.params.teamIds}
          navigation={props.navigation}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const MIN_ZOOM = 0.5; // 50% - kompakt vy
const MAX_ZOOM = 3.0; // 300% - mycket detaljerad vy
const DEFAULT_ZOOM = 1.0; // 100% - standard
const ZOOM_STEP = 0.5; // Stegstorlek för zoom-knappar

function EmployeeScheduleMainScreenComponent(props: {
  fetchKey: string;
  teamIds: string[];
  navigation: UserLoggedInNavigatorNavigationProp<"EmployeeScheduleEdit">;
}) {
  const data = useLazyLoadQuery<EmployeeScheduleScreenQuery>(
    graphql`
      query EmployeeScheduleScreenQuery($teamIds: [ID!]!) {
        store {
          id
          ...usePermissions_store
          teams(ids: $teamIds) {
            scheduleTypes {
              id
            }
          }
          scheduledEmployees(teamIds: $teamIds) {
            id
            ...EmployeeScheduleNameEmployeeNamePanel_employee
            ...EmployeeScheduleShiftsEmployee_employee
          }
        }
      }
    `,
    { teamIds: props.teamIds },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );
  const intl_tokens = useIntlTokens();
  const hasTeamAdmin = useHasPermissions(data.store, "TEAM_ADMIN");

  function onRequestAdmin() {
    if (props.teamIds.length === 1) {
      props.navigation.navigate("EmployeeScheduleEdit", {
        screen: "EmployeeScheduleEditScheduleTypesAssignTeam",
        params: {
          teamId: props.teamIds[0],
          teamName: "",
        },
      });
    } else {
      props.navigation.navigate("EmployeeScheduleEdit", {});
    }
  }

  useNavigationHeader({
    type: "CLOSE",
    onClose: () => {
      props.navigation.goBack();
    },
    disableBorder: true,
    title: intl_tokens.EmployeeSchedule,
    more: hasTeamAdmin
      ? [
          {
            label: intl_tokens.AssignScheduleTypes,
            icon: "settings",
            type: "navigation",
            onPress: onRequestAdmin,
          },
        ]
      : [],
  });

  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const baseWidth = isCompact ? 1050 : 1500;
  const width = baseWidth * zoomLevel;
  const boxWidth = width / (totalHours * 2);

  const employees = data.store?.scheduledEmployees ?? [];
  type Item = (typeof employees)[number];

  const scheduleScrollViewRef = useAnimatedRef<Animated.ScrollView>();
  const scheduleFlatListRef = useAnimatedRef<Animated.FlatList<Item>>();
  const scrollX = useSharedValue(0);
  const scrollY = useSharedValue(0);

  const handleZoomChange = (newZoom: number) => {
    const scrollRatioX = scrollX.value / (baseWidth * zoomLevel);
    setZoomLevel(newZoom);
    scheduleOnUI(() => {
      "worklet";
      const newWidth = baseWidth * newZoom;
      const newScrollX = scrollRatioX * newWidth;
      scrollTo(scheduleScrollViewRef, newScrollX, 0, false);
    });
  };

  const zoomIn = () => {
    const newZoom = Math.min(zoomLevel + ZOOM_STEP, MAX_ZOOM);
    handleZoomChange(newZoom);
  };

  const zoomOut = () => {
    const newZoom = Math.max(zoomLevel - ZOOM_STEP, MIN_ZOOM);
    handleZoomChange(newZoom);
  };

  const sf = useScaleFactor();

  const doubleListHeight = Math.floor(35 * sf);

  const listItemHeight = Math.floor(doubleListHeight);

  const headerHeight = 20 * sf;

  const startHour = 5;

  const startMins = startHour * 60;
  const startD = useMemo(
    () =>
      DateTime.local().set({
        hour: startHour,
        minute: 0,
        second: 0,
        millisecond: 0,
      }),
    [startHour]
  );
  const renderEmployee = useCallback(
    ({ item }: { item: Item }) => (
      <Employee
        key={item.id}
        employee={item}
        boxWidth={boxWidth}
        dayStartMinutes={startMins}
        listItemHeight={listItemHeight}
      />
    ),
    [boxWidth, listItemHeight, startMins]
  );
  const renderEmployeeName = useCallback(
    ({ item }: { item: Item }) => (
      <NamePanel employee={item} height={listItemHeight} />
    ),
    [listItemHeight]
  );

  const nameFlatList = useAnimatedRef<Animated.FlatList<Item>>();

  const onHorizontalScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollX.value = event.contentOffset.x;
    },
  });

  const onVerticalScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.value = event.contentOffset.y;
      scrollTo(nameFlatList, 0, event.contentOffset.y, false);
    },
  });
  const inset = useSafeAreaInsets();
  const layout = useLayoutContext();
  const theme = useTheme();

  const nameWidth = isCompact ? 90 : 120;

  const hasAssignedCostCenters = data.store.teams.some(
    (t) => t.scheduleTypes.length > 0
  );
  const showAssignScheduleTypeCTA = !hasAssignedCostCenters && hasTeamAdmin;

  return (
    <Box style={{ flex: 1 }}>
      {showAssignScheduleTypeCTA ? (
        <>
          <Box
            color="surface-background-light"
            space="medium"
            borderColor="border-primary"
            style={{
              borderTopWidth: 1,
              borderBottomWidth: 1,
            }}
          >
            <Box
              style={{
                maxWidth: 400,
                flex: 1,
                width: "100%",
              }}
            >
              <ListItem
                left={<AHIcon type="settings" />}
                right={<ListItem.NavArrow />}
                isFree
                border="border-primary"
                onPress={() => {
                  onRequestAdmin();
                }}
              >
                <ListItem.Title>
                  {intl_tokens.AssignScheduleTypes}
                </ListItem.Title>
              </ListItem>
            </Box>
          </Box>
          <Box style={{ width: "100%" }} color="surface" spaceTop="small"></Box>
        </>
      ) : null}
      <Box
        color="surface-background-light"
        style={{ flex: 1, flexDirection: "row" }}
      >
        <View
          style={{
            maxWidth: nameWidth,
            flex: 1,
            paddingTop: headerHeight,

            backgroundColor: theme.surface,
          }}
        >
          <Animated.FlatList
            ref={nameFlatList}
            data={employees}
            renderItem={renderEmployeeName}
            scrollEnabled={false}
            contentInset={{ top: 2000, bottom: 20000 }}
            contentInsetAdjustmentBehavior={"never"}
            contentOffset={{ y: 0, x: 0 }}
            contentContainerStyle={{
              paddingLeft: layout.space.small,
              paddingBottom: 16,
            }}
            showsVerticalScrollIndicator={false}
          />
        </View>
        <Animated.ScrollView
          ref={scheduleScrollViewRef}
          horizontal={true}
          style={{ flex: 1 }}
          onScroll={onHorizontalScroll}
          scrollEventThrottle={16}
        >
          <TimeBackgroundLines
            headerHeight={headerHeight}
            left={0}
            boxWidth={boxWidth}
            startD={startD}
          />
          <Animated.FlatList
            ref={scheduleFlatListRef}
            automaticallyAdjustContentInsets={false}
            showsVerticalScrollIndicator={false}
            data={employees}
            stickyHeaderIndices={[0]}
            keyExtractor={(item) => item.id}
            scrollEventThrottle={16}
            onScroll={onVerticalScroll}
            ListHeaderComponent={
              <TimeHeader
                headerHeight={headerHeight}
                boxWidth={boxWidth}
                employeePaneWidth={0}
              />
            }
            renderItem={renderEmployee}
            contentInset={{ bottom: inset.bottom }}
            style={{ width: width }}
          />
        </Animated.ScrollView>
        <ZoomControls
          zoomLevel={zoomLevel}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
        />
      </Box>
    </Box>
  );
}

const TimeHeader = React.memo(InnerTimeHeader);
function InnerTimeHeader(props: {
  headerHeight: number;
  employeePaneWidth: number;
  boxWidth: number;
}) {
  return (
    <Box
      color="surface"
      style={{
        flexDirection: "row",
        height: props.headerHeight,

        borderBottomWidth: 1,
      }}
      borderColor="border-primary"
    >
      {new Array(totalHours - 1).fill(0).map((_, i) => {
        return (
          <AHText
            key={i + ""}
            style={{
              width: props.boxWidth * 2,
              flex: 0,
              left: i * props.boxWidth * 2 + props.boxWidth,
              textAlign: "center",
              top: 0,

              position: "absolute",
            }}
            numberOfLines={1}
            font={AHTextFonts.BOLDER}
            color="darkerText"
            type="detail"
          >
            {("00" + Math.floor(i + 6)).slice(-2)}
          </AHText>
        );
      })}
    </Box>
  );
}
